// src/pages/InvitationManagement.js
import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import { toast } from "react-toastify";
import {
  createInvitation,
  listInvitations,
  revokeInvitation,
  listRoles,
  getRegisteredCameras,
} from "../Api";
import { getOrganizationId } from "../utils/authUtils";
import { FaCopy, FaCheck, FaTimes } from "react-icons/fa";
import "./InvitationManagement.css";

export default function InvitationManagement() {
  const [roles, setRoles] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [isLoadingInvitations, setIsLoadingInvitations] = useState(true);
  const [isLoadingCameras, setIsLoadingCameras] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdInvitation, setCreatedInvitation] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const [formData, setFormData] = useState({
    roleId: "",
    expiresInDays: 7,
    cameraIds: [], // New: selected camera IDs
  });

  useEffect(() => {
    fetchRoles();
    fetchInvitations();
    fetchCameras();
  }, []);

  const fetchRoles = async () => {
    setIsLoadingRoles(true);
    try {
      const data = await listRoles();
      setRoles(data || []);
    } catch (error) {
      console.error("Failed to fetch roles:", error);
      toast.error("Failed to load roles");
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const fetchInvitations = async () => {
    setIsLoadingInvitations(true);
    try {
      const orgId = getOrganizationId();
      if (!orgId) {
        toast.error("Organization ID not found");
        return;
      }

      const data = await listInvitations(orgId);
      setInvitations(data || []);
    } catch (error) {
      console.error("Failed to fetch invitations:", error);
      toast.error("Failed to load invitations");
    } finally {
      setIsLoadingInvitations(false);
    }
  };

  const fetchCameras = async () => {
    setIsLoadingCameras(true);
    try {
      const data = await getRegisteredCameras();
      setCameras(data || []);
    } catch (error) {
      console.error("Failed to fetch cameras:", error);
      toast.error("Failed to load cameras");
    } finally {
      setIsLoadingCameras(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCameraToggle = (cameraId) => {
    setFormData((prev) => {
      const isSelected = prev.cameraIds.includes(cameraId);
      if (isSelected) {
        return {
          ...prev,
          cameraIds: prev.cameraIds.filter((id) => id !== cameraId),
        };
      } else {
        return {
          ...prev,
          cameraIds: [...prev.cameraIds, cameraId],
        };
      }
    });
  };

  const handleSelectAllCameras = () => {
    if (formData.cameraIds.length === cameras.length) {
      // Deselect all
      setFormData((prev) => ({ ...prev, cameraIds: [] }));
    } else {
      // Select all
      setFormData((prev) => ({
        ...prev,
        cameraIds: cameras.map((cam) => cam.id),
      }));
    }
  };

  const handleCreateInvitation = async (e) => {
    e.preventDefault();

    if (!formData.roleId) {
      toast.error("Please select a role");
      return;
    }

    setIsCreating(true);
    try {
      const orgId = getOrganizationId();
      const invitationData = {
        organization_id: orgId,
        role_id: formData.roleId,
        expires_in_days: parseInt(formData.expiresInDays),
        camera_ids: formData.cameraIds.length > 0 ? formData.cameraIds : undefined,
      };

      const result = await createInvitation(orgId, invitationData);
      setCreatedInvitation(result);
      setShowSuccessModal(true);

      // Reset form
      setFormData({
        roleId: "",
        expiresInDays: 7,
        cameraIds: [],
      });

      // Refresh invitations list
      fetchInvitations();
      toast.success("Invitation created successfully");
    } catch (error) {
      console.error("Failed to create invitation:", error);
      toast.error("Failed to create invitation");
    } finally {
      setIsCreating(false);
    }
  };

  const handleRevokeInvitation = async (code) => {
    if (!window.confirm("Are you sure you want to revoke this invitation?")) {
      return;
    }

    try {
      await revokeInvitation(code);
      toast.success("Invitation revoked successfully");
      fetchInvitations();
    } catch (error) {
      console.error("Failed to revoke invitation:", error);
      toast.error("Failed to revoke invitation");
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (invitation) => {
    if (!invitation.is_active) {
      return <span className="status-badge status-revoked">Used/Revoked</span>;
    }
    if (invitation.is_expired) {
      return <span className="status-badge status-expired">Expired</span>;
    }
    return <span className="status-badge status-active">Active</span>;
  };

  const truncateCode = (code) => {
    if (!code || code.length <= 20) return code;
    return `${code.substring(0, 10)}...${code.substring(code.length - 6)}`;
  };

  return (
    <div className="app-container">
      <Sidebar />
      <main className="main-section">
        <div className="invitation-management-container">
          <h1 className="page-header">Invitation Management</h1>

          {/* Create Invitation Card */}
          <div className="invitation-card">
            <div className="card-header">
              <h2>Create New Invitation</h2>
            </div>
            <div className="card-body">
              <form onSubmit={handleCreateInvitation} className="invitation-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="roleId" className="form-label">
                      Role *
                    </label>
                    {isLoadingRoles ? (
                      <div className="loading-text">Loading roles...</div>
                    ) : (
                      <select
                        id="roleId"
                        className="form-select"
                        value={formData.roleId}
                        onChange={(e) => handleInputChange("roleId", e.target.value)}
                        disabled={isCreating}
                        required
                      >
                        <option value="">Select a role</option>
                        {roles.map((role) => (
                          <option key={role.role_id} value={role.role_id}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="expiresInDays" className="form-label">
                      Expires In (Days)
                    </label>
                    <input
                      id="expiresInDays"
                      type="number"
                      className="form-input"
                      value={formData.expiresInDays}
                      onChange={(e) =>
                        handleInputChange("expiresInDays", e.target.value)
                      }
                      min="1"
                      max="365"
                      disabled={isCreating}
                      required
                    />
                  </div>
                </div>

                {/* Camera Restrictions Section */}
                <div className="form-group-full">
                  <label className="form-label">
                    Camera Restrictions (Optional)
                  </label>
                  <p className="form-help-text">
                    Leave empty for full access to all cameras. Select specific cameras to restrict user access.
                  </p>

                  {isLoadingCameras ? (
                    <div className="loading-text">Loading cameras...</div>
                  ) : cameras.length === 0 ? (
                    <div className="empty-state-small">No cameras registered</div>
                  ) : (
                    <div className="camera-selection-container">
                      <div className="camera-selection-header">
                        <span className="selected-count">
                          {formData.cameraIds.length === 0
                            ? "All cameras (unrestricted)"
                            : `${formData.cameraIds.length} of ${cameras.length} selected`}
                        </span>
                        <button
                          type="button"
                          className="btn-link"
                          onClick={handleSelectAllCameras}
                          disabled={isCreating}
                        >
                          {formData.cameraIds.length === cameras.length
                            ? "Deselect All"
                            : "Select All"}
                        </button>
                      </div>
                      <div className="camera-checkbox-list">
                        {cameras.map((camera) => (
                          <label key={camera.id} className="camera-checkbox-item">
                            <input
                              type="checkbox"
                              checked={formData.cameraIds.includes(camera.id)}
                              onChange={() => handleCameraToggle(camera.id)}
                              disabled={isCreating}
                            />
                            <span className="camera-name">{camera.name}</span>
                            <span className="camera-location">
                              {camera.location || "No location"}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className="btn-primary btn-full-width"
                  disabled={isCreating || isLoadingRoles}
                >
                  {isCreating ? "Creating..." : "Generate Invitation"}
                </button>
              </form>
            </div>
          </div>

          {/* Invitations Table Card */}
          <div className="invitation-card">
            <div className="card-header">
              <h2>Active Invitations</h2>
            </div>
            <div className="card-body">
              {isLoadingInvitations ? (
                <div className="loading-text">Loading invitations...</div>
              ) : invitations.length === 0 ? (
                <div className="empty-state">
                  No invitations found. Create one to get started!
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="invitations-table">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Role</th>
                        <th>Camera Access</th>
                        <th>Created</th>
                        <th>Expires</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invitations.map((inv) => (
                        <tr key={inv.code}>
                          <td>
                            <div className="code-cell">
                              <span className="code-text" title={inv.code}>
                                {truncateCode(inv.code)}
                              </span>
                              <button
                                className="btn-icon"
                                onClick={() => copyToClipboard(inv.code, inv.code)}
                                title="Copy code"
                              >
                                {copiedField === inv.code ? (
                                  <FaCheck className="icon-success" />
                                ) : (
                                  <FaCopy />
                                )}
                              </button>
                            </div>
                          </td>
                          <td>{inv.role_name}</td>
                          <td>
                            {inv.is_camera_restricted ? (
                              <span className="camera-access-badge restricted">
                                {inv.permitted_camera_ids?.length || 0} cameras
                              </span>
                            ) : (
                              <span className="camera-access-badge unrestricted">
                                All cameras
                              </span>
                            )}
                          </td>
                          <td>{formatDate(inv.created_at)}</td>
                          <td>{formatDate(inv.expires_at)}</td>
                          <td>{getStatusBadge(inv)}</td>
                          <td>
                            <button
                              className="btn-danger-small"
                              onClick={() => handleRevokeInvitation(inv.code)}
                              disabled={!inv.is_active}
                            >
                              Revoke
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Success Modal */}
        {showSuccessModal && createdInvitation && (
          <div className="modal-overlay" onClick={() => setShowSuccessModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Invitation Created Successfully!</h2>
                <button
                  className="modal-close"
                  onClick={() => setShowSuccessModal(false)}
                >
                  <FaTimes />
                </button>
              </div>
              <div className="modal-body">
                <div className="modal-field">
                  <label>Invitation Code</label>
                  <div className="copy-field">
                    <input
                      type="text"
                      value={createdInvitation.code}
                      readOnly
                      className="modal-input"
                    />
                    <button
                      className="btn-copy"
                      onClick={() =>
                        copyToClipboard(createdInvitation.code, "modal-code")
                      }
                    >
                      {copiedField === "modal-code" ? <FaCheck /> : <FaCopy />}
                      {copiedField === "modal-code" ? " Copied" : " Copy"}
                    </button>
                  </div>
                </div>

                <div className="modal-field">
                  <label>Invitation URL</label>
                  <div className="copy-field">
                    <input
                      type="text"
                      value={createdInvitation.invitation_url}
                      readOnly
                      className="modal-input"
                    />
                    <button
                      className="btn-copy"
                      onClick={() =>
                        copyToClipboard(
                          createdInvitation.invitation_url,
                          "modal-url"
                        )
                      }
                    >
                      {copiedField === "modal-url" ? <FaCheck /> : <FaCopy />}
                      {copiedField === "modal-url" ? " Copied" : " Copy"}
                    </button>
                  </div>
                </div>

                <div className="modal-info">
                  <p>
                    <strong>Role:</strong> {createdInvitation.role_name}
                  </p>
                  <p>
                    <strong>Expires:</strong> {formatDate(createdInvitation.expires_at)}
                  </p>
                  <p>
                    <strong>Camera Access:</strong>{" "}
                    {createdInvitation.is_camera_restricted ? (
                      <span className="text-warning">
                        Restricted to {createdInvitation.permitted_camera_ids?.length || 0} camera(s)
                      </span>
                    ) : (
                      <span className="text-success">All cameras (unrestricted)</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn-primary"
                  onClick={() => setShowSuccessModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}